import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Popconfirm, Row, Select } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField, TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { EIso3166OneAlpha2 } from "@devowl-wp/iso-codes";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { spacing } from "../../../../../../styles/spacing.js";
import { mergeStyles } from "../../../../../../util/style.js";
import { defaultTranslationFlexNamePath, isExtendedFieldHidden } from "../../../../../../util/template/form.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { isValidPhoneNumber } from "../../../../../../util/template/phone.js";
import { getProviderPropertyTranslation } from "../../../../../../util/template/provider.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { normalizeUrlWithProtocol } from "../../../../../../util/template/url.js";
import { getInputPropsByLanguage, getSelectPropsByLanguage, getTranslationFlagElementStyle, hasTranslationFlagsChanged, onTranslationValueChange } from "../../../../../../util/translation.js";
import { LanguageIcon } from "../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationInputFormFields } from "../../fields/translation/language-input/language-input-form-fields.js";
import { TranslationSelectFormFields } from "../../fields/translation/language-select-input/language-select-form-fields.js";
import { LanguageSelect } from "../../fields/translation/language-select.js";
import { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
/**
 * Form field for the provider of a service template.
 * Provider and contact information is important for the cookie banner.
 *
 * @returns The form field.
 */ const FormFieldServiceTemplateProvider = observer(()=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const { isTranslationMode } = translationSettings;
    // Triggers form item re-render on translation mode toggle
    const lastKnownTranslationMode = isTranslationMode;
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const form = Form.useFormInstance();
    const labelColumnStyle = {
        style: {
            width: "10em"
        }
    };
    const keepContentFormStyle = {
        marginBottom: 0,
        marginTop: 0
    };
    const countryOptions = useMemo(()=>Object.keys(EIso3166OneAlpha2).map((country)=>{
            if (isNaN(Number(country))) {
                return {
                    value: EIso3166OneAlpha2[country],
                    label: country.replace(/([a-z])([A-Z])/g, "$1 $2")
                };
            }
            return undefined;
        }).filter(Boolean), []);
    const handleCountrySelection = useCallback((getFieldValue, setFieldValue, selectedCountry)=>{
        if (selectedCountry) {
            const dataProcessingInCountries = getFieldValue([
                ServiceTemplateField.DataProcessingInCountries
            ]);
            const updated = [
                ...dataProcessingInCountries || []
            ];
            if (!updated.includes(selectedCountry)) {
                updated.push(selectedCountry);
                setFieldValue([
                    ServiceTemplateField.DataProcessingInCountries
                ], updated);
            }
        }
    }, []);
    const getCustomFormItemName = useCallback((subTarget)=>{
        return (target)=>[
                ServiceTemplateField.Provider,
                TRANSLATIONS_PROPERTY_NAME,
                target,
                subTarget
            ];
    }, [
        ServiceTemplateField.Provider
    ]);
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapperByCdn, {
        children: /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
            fieldNames: ServiceTemplateField.Provider,
            permissionCheck: {
                whenRole: EUserRole.Translator,
                roleWhiteList: TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST
            },
            forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.Provider, hasCreatorRole),
            children: /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prev, next)=>prev.isProviderCurrentWebsite !== next.isProviderCurrentWebsite || prev.extends?.identifier !== next.extends?.identifier || prev.extendsIdentifier !== next.extendsIdentifier,
                children: ({ getFieldValue: getFieldValueStrict, setFieldValue: setFieldValueStrict })=>{
                    const getFieldValue = getFieldValueStrict;
                    const setFieldValue = setFieldValueStrict;
                    const onCopy = (options)=>{
                        let sourceValue = undefined;
                        if (options.fromLanguage === DEFAULT_LANGUAGE) {
                            sourceValue = getFieldValue(ServiceTemplateField.Provider);
                        } else {
                            sourceValue = getFieldValue([
                                ServiceTemplateField.Provider,
                                TRANSLATIONS_PROPERTY_NAME,
                                options.fromLanguage
                            ]);
                        }
                        if (sourceValue) {
                            setFieldValue([
                                ServiceTemplateField.Provider,
                                TRANSLATIONS_PROPERTY_NAME,
                                options.toLanguage
                            ], structuredClone(sourceValue));
                        }
                    };
                    return getFieldValue(ServiceTemplateField.IsProviderCurrentWebsite) === false && /*#__PURE__*/ _jsx(Form.Item, {
                        label: getFieldNameTranslated(ServiceTemplateField.Provider, t),
                        shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier || prev.provider !== next.provider || hasTranslationFlagsChanged(prev, next, ServiceTemplateField.Provider) || lastKnownTranslationMode !== isTranslationMode,
                        required: true,
                        children: ({ getFieldValue: getFieldValueStrict, setFieldValue: setFieldValueStrict })=>{
                            const getFieldValue = getFieldValueStrict;
                            const setFieldValue = setFieldValueStrict;
                            const extended = getFieldValue(TemplateMetaDataField.Extends);
                            const extendedProvider = extended?.provider;
                            const linkRules = [
                                {
                                    type: "url",
                                    message: t("Please provide a valid url.")
                                }
                            ];
                            const linkNormalize = (value)=>normalizeUrlWithProtocol(value);
                            const phoneRules = [
                                {
                                    validator (_, value) {
                                        if (isValidPhoneNumber(value)) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject(new Error(t("Please provide a valid phone number.")));
                                        }
                                    }
                                }
                            ];
                            const emailRules = [
                                {
                                    type: "email",
                                    message: t("Please give an email, formatted correctly.")
                                }
                            ];
                            const mailNormalize = (value)=>value.trim();
                            const countrySelectFilterOption = (input, option)=>String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());
                            const onCountryChange = (country)=>handleCountrySelection(getFieldValue, setFieldValue, country);
                            const inputPropsDefaultLanguage = getInputPropsByLanguage(DEFAULT_LANGUAGE, translationSettings);
                            const translationFlagStyleDefaultLanguage = getTranslationFlagElementStyle(form, ServiceTemplateField.Provider);
                            inputPropsDefaultLanguage.onChange = ()=>onTranslationValueChange(form, ServiceTemplateField.Provider);
                            const translationFlagStyle = getTranslationFlagElementStyle(form, ServiceTemplateField.Provider);
                            inputPropsDefaultLanguage.style = mergeStyles(inputPropsDefaultLanguage.style, translationFlagStyle);
                            // language icon
                            inputPropsDefaultLanguage.suffix = /*#__PURE__*/ _jsx(LanguageIcon, {
                                lang: DEFAULT_LANGUAGE,
                                flexPathConfig: {
                                    isDistinctFlagPath: true,
                                    absolute: {
                                        namePath: ServiceTemplateField.Provider,
                                        translationNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                    }
                                }
                            });
                            const selectPropsDefaultLanguage = getSelectPropsByLanguage(DEFAULT_LANGUAGE, translationSettings);
                            selectPropsDefaultLanguage.style = {
                                ...selectPropsDefaultLanguage.style,
                                ...translationFlagStyleDefaultLanguage
                            };
                            return getFieldValue(ServiceTemplateField.IsProviderCurrentWebsite) === false && /*#__PURE__*/ _jsxs(TemplateMetaDataExtendsStrategyFormField, {
                                extendsFieldName: ServiceTemplateField.Provider,
                                disableStrategies: getDisableStrategies(),
                                onKeepContent: extended && extendedProvider && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        Object.keys(extendedProvider).map((propertyName)=>{
                                            return /*#__PURE__*/ _jsx(Form.Item, {
                                                label: getProviderPropertyTranslation(t, propertyName),
                                                labelCol: labelColumnStyle,
                                                style: keepContentFormStyle,
                                                children: extendedProvider[propertyName]
                                            }, `provider-extended-${propertyName}`);
                                        }),
                                        /*#__PURE__*/ _jsx("br", {})
                                    ]
                                }),
                                children: [
                                    /*#__PURE__*/ _jsx(CopyFromProviderRow, {
                                        onCopy: onCopy
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "name"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "name"
                                                ],
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("name"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "streetAndNumber"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "streetAndNumber"
                                                ],
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("streetAndNumber"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "placeAndPostcode"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "placeAndPostcode"
                                                ],
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("placeAndPostcode"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "country"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsxs(Row, {
                                                gutter: [
                                                    10,
                                                    10
                                                ],
                                                style: {
                                                    flexFlow: "row"
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(Col, {
                                                        flex: 1,
                                                        children: /*#__PURE__*/ _jsx(Form.Item, {
                                                            name: [
                                                                ServiceTemplateField.Provider,
                                                                "country"
                                                            ],
                                                            hasFeedback: true,
                                                            noStyle: true,
                                                            children: /*#__PURE__*/ _jsx(Select, {
                                                                showSearch: true,
                                                                allowClear: true,
                                                                placeholder: t("Select a country"),
                                                                optionFilterProp: "children",
                                                                filterOption: countrySelectFilterOption,
                                                                options: countryOptions,
                                                                ...selectPropsDefaultLanguage,
                                                                onChange: (value)=>{
                                                                    onCountryChange(value);
                                                                    onTranslationValueChange(form, ServiceTemplateField.Provider);
                                                                },
                                                                disabled: !hasCreatorRole
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(Col, {
                                                        flex: "0",
                                                        children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                                            lang: DEFAULT_LANGUAGE,
                                                            flexPathConfig: {
                                                                isDistinctFlagPath: true,
                                                                absolute: {
                                                                    namePath: getCustomFormItemName("country"),
                                                                    customFlagNamePath: ServiceTemplateField.Provider,
                                                                    translationNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                                }
                                                            }
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationSelectFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("country"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                },
                                                selectProps: {
                                                    showSearch: true,
                                                    allowClear: true,
                                                    optionFilterProp: "children",
                                                    filterOption: countrySelectFilterOption,
                                                    options: countryOptions,
                                                    onChange: onCountryChange
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "email"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "email"
                                                ],
                                                rules: emailRules,
                                                normalize: mailNormalize,
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("email"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                },
                                                formProps: {
                                                    rules: emailRules,
                                                    normalize: mailNormalize
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "phone"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "phone"
                                                ],
                                                rules: phoneRules,
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("phone"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                },
                                                formProps: {
                                                    rules: phoneRules
                                                }
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Form.Item, {
                                        label: getProviderPropertyTranslation(t, "link"),
                                        labelCol: labelColumnStyle,
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: [
                                                    ServiceTemplateField.Provider,
                                                    "link"
                                                ],
                                                rules: linkRules,
                                                normalize: linkNormalize,
                                                hasFeedback: true,
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Input, {
                                                    ...inputPropsDefaultLanguage,
                                                    disabled: !hasCreatorRole,
                                                    onChange: ()=>onTranslationValueChange(form, ServiceTemplateField.Provider)
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationInputFormFields, {
                                                flexPathConfig: {
                                                    isDistinctFlagPath: true,
                                                    absolute: {
                                                        namePath: getCustomFormItemName("link"),
                                                        customFlagNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.Provider)
                                                    }
                                                },
                                                formProps: {
                                                    rules: linkRules,
                                                    normalize: linkNormalize
                                                }
                                            })
                                        ]
                                    })
                                ]
                            });
                        }
                    });
                }
            })
        })
    });
});
const CopyFromProviderRow = observer((props)=>{
    const { t } = useTranslation();
    const { rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const { targetLanguage } = translationSettings;
    const [copyFromLanguage, setCopyFromLanguage] = useState(DEFAULT_LANGUAGE);
    const [copyToLanguage, setCopyToLanguage] = useState(targetLanguage);
    const selectColumnStyle = {
        width: "12rem",
        marginLeft: spacing.layout.content.margin,
        marginRight: spacing.layout.content.margin
    };
    useEffect(()=>{
        setCopyToLanguage(targetLanguage);
    }, [
        targetLanguage
    ]);
    return /*#__PURE__*/ _jsxs(Row, {
        hidden: !translationSettings.isTranslationMode,
        style: {
            borderTop: "1px solid lightgrey",
            paddingTop: spacing.layout.content.paddingSmall,
            paddingBottom: spacing.layout.content.paddingSmall,
            alignItems: "baseline"
        },
        children: [
            /*#__PURE__*/ _jsx(Col, {
                style: {
                    flex: "1 1"
                }
            }),
            /*#__PURE__*/ _jsxs(Col, {
                children: [
                    t("Copy from"),
                    ":"
                ]
            }),
            /*#__PURE__*/ _jsx(Col, {
                style: selectColumnStyle,
                children: /*#__PURE__*/ _jsx(LanguageSelect, {
                    excludedLang: [
                        copyToLanguage
                    ],
                    onChange: (selectedSourceLanguage)=>{
                        setCopyFromLanguage(selectedSourceLanguage);
                    }
                })
            }),
            /*#__PURE__*/ _jsxs(Col, {
                children: [
                    t("to"),
                    ":"
                ]
            }),
            /*#__PURE__*/ _jsx(Col, {
                style: selectColumnStyle,
                children: /*#__PURE__*/ _jsx(LanguageSelect, {
                    value: copyToLanguage,
                    excludedLang: [
                        copyFromLanguage
                    ],
                    onChange: (selectedToLanguage)=>{
                        setCopyToLanguage(selectedToLanguage);
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Col, {
                style: {
                    marginLeft: spacing.layout.content.margin
                },
                children: /*#__PURE__*/ _jsx(Popconfirm, {
                    placement: "bottom",
                    title: t("Overwrite current data?"),
                    onConfirm: ()=>props.onCopy({
                            fromLanguage: copyFromLanguage,
                            toLanguage: copyToLanguage
                        }),
                    children: /*#__PURE__*/ _jsx(Button, {
                        icon: /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                        type: "link",
                        size: "small",
                        children: t("Copy")
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Col, {
                style: {
                    flex: "1 1"
                }
            })
        ]
    });
});
export { FormFieldServiceTemplateProvider };
